// src/components/Photographs.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import "./photographs.css";
import AudioFloat from "./AudioFloat.js";

/* -------------------------------------
   Body data attribute (light nav mode)
------------------------------------- */
function useLightNav(flag = true) {
  useEffect(() => {
    const el = document?.body;
    if (!el) return;
    if (flag) el.setAttribute("data-light-nav", "true");
    return () => el.removeAttribute("data-light-nav");
  }, [flag]);
}

/* -------------------------------------
   Detect mobile vs desktop
------------------------------------- */
function useIsMobile(query = "(max-width: 991px)") {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const on = (e) => setIsMobile(e.matches);

    mql.addEventListener
      ? mql.addEventListener("change", on)
      : mql.addListener(on);

    return () => {
      mql.removeEventListener
        ? mql.removeEventListener("change", on)
        : mql.removeListener(on);
    };
  }, [query]);

  return isMobile;
}

/* -------------------------------------
   Reveal on scroll
------------------------------------- */
function useRevealOnScroll(deps = []) {
  useEffect(() => {
    const nodes = document.querySelectorAll(".photographs-page .reveal");
    if (!nodes.length) return;

    if (!("IntersectionObserver" in window)) {
      nodes.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries, obs) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "10% 0px -4% 0px" }
    );

    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, deps);
}

/* -------------------------------------
   Helpers
------------------------------------- */
function getOrder(p) {
  const raw =
    (p?.order && (p.order.$numberInt ?? p.order.$numberLong)) ??
    p?.order ??
    9_999_999;

  if (typeof raw === "number") return raw;

  if (typeof raw === "string") {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 9_999_999;
  }

  return 9_999_999;
}

/* -------------------------------------
   Component
------------------------------------- */
export default function Photographs({ fetchPhotographs, photographs }) {
  useLightNav(true);
  const isMobile = useIsMobile();

  // how many images reported load/error (still useful for framed auto-init)
  const [loadedCount, setLoadedCount] = useState(0);

  /* ---------- persistent prefs ---------- */
  const [mobileCols, setMobileCols] = useState(() => {
    try {
      return Number(localStorage.getItem("lvl_photos_mobile_cols")) || 1;
    } catch {
      return 1;
    }
  });

  const [desktopCols, setDesktopCols] = useState(() => {
    try {
      return Number(localStorage.getItem("lvl_photos_desktop_cols")) || 3;
    } catch {
      return 3;
    }
  });

  const [initialFramedPref] = useState(() => {
    try {
      return localStorage.getItem("lvl_grid_frame") === "1";
    } catch {
      return false;
    }
  });

  const [matSize, setMatSize] = useState(() => {
    try {
      return localStorage.getItem("lvl_grid_mat") || "standard";
    } catch {
      return "standard";
    }
  }); // "standard" | "large"

  const [framed, setFramed] = useState(false);
  const [didInitFrame, setDidInitFrame] = useState(false);

  /* ---------- persist prefs ---------- */
  useEffect(() => {
    try {
      localStorage.setItem("lvl_photos_mobile_cols", String(mobileCols));
    } catch {}
  }, [mobileCols]);

  useEffect(() => {
    try {
      localStorage.setItem("lvl_photos_desktop_cols", String(desktopCols));
    } catch {}
  }, [desktopCols]);

  useEffect(() => {
    try {
      localStorage.setItem("lvl_grid_frame", framed ? "1" : "0");
    } catch {}
  }, [framed]);

  useEffect(() => {
    try {
      localStorage.setItem("lvl_grid_mat", matSize);
    } catch {}
  }, [matSize]);

  /* ---------- fetch photographs on mount ---------- */
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof fetchPhotographs === "function") {
      fetchPhotographs();
    }
  }, [fetchPhotographs]);

  /* ---------- normalize + curate + sort ---------- */
  const list = useMemo(() => {
    if (!photographs) return [];
    const arr = Array.isArray(photographs)
      ? photographs
      : Object.values(photographs);

    const filtered = arr.filter((p) => {
      if (!p) return false;
      if (p.cause === true) return false;

      const {
        wildlife,
        landscape,
        print,
        childlabor,
        causes,
        antarctica,
        journey,
        vessel,
        fading,
      } = p;

      if (wildlife || landscape || print || childlabor || causes) return false;
      if (antarctica || journey || vessel || fading) return false;
      return !!p.imageURL;
    });

    return filtered.sort((a, b) => getOrder(a) - getOrder(b));
  }, [photographs]);

  /* ---------- auto-enable framed after load (once) ---------- */
  useEffect(() => {
    if (
      initialFramedPref &&
      !didInitFrame &&
      !framed &&
      list.length > 0 &&
      loadedCount >= list.length
    ) {
      setFramed(true);
      setDidInitFrame(true);
    }
  }, [initialFramedPref, didInitFrame, framed, loadedCount, list.length]);

  /* ---------- scroll reveal ---------- */
  useRevealOnScroll([list, mobileCols, desktopCols, framed, matSize, isMobile]);

  /* ---------- per-image load ---------- */
  const handleImgLoad = useCallback((e) => {
    const img = e.currentTarget;
    img.classList.add("is-loaded");

    const article = img.closest(".ph-card");
    const slot = img.closest(".card-link");

    if (article) {
      const { naturalWidth: w, naturalHeight: h } = img;
      const isLandscape = w >= h;

      if (slot) {
        slot.classList.toggle("is-landscape", isLandscape);
        slot.classList.toggle("is-portrait", !isLandscape);
        slot.setAttribute(
          "data-shape",
          isLandscape ? "landscape" : "portrait"
        );
      }

      article.classList.toggle("is-landscape", isLandscape);
      article.classList.toggle("is-portrait", !isLandscape);
      article.setAttribute(
          "data-shape",
          isLandscape ? "landscape" : "portrait"
      );
    }

    setLoadedCount((c) => c + 1);
  }, []);

  /* ---------- classes ---------- */
  const gridClasses = [
    "photographs-grid",
    isMobile
      ? mobileCols === 2
        ? "m-two"
        : "m-one"
      : desktopCols === 2
      ? "d-two"
      : "d-three",
    framed ? "framed-on" : "framed-off",
    framed ? (matSize === "large" ? "mat-large" : "mat-standard") : "",
  ]
    .filter(Boolean)
    .join(" ");

  // no intro gate anymore, so no conditional class
 const rootClass = "photographs-page page-is-ready";

  return (
    <div className={rootClass}>
      {/* MAIN PAGE CONTENT */}
      <div className="main-page page-pad">
        {/* Title / subcopy */}
        <header className="page-head reveal">
          <h1 className="page-title-centered">Portrait Study</h1>
          <p className="centered-sub">
            An ongoing record of human presence. Each work is photographed
            on location and finished in limited edition fine art print.
            Touch any piece to view scale, provenance, and acquisition
            details.
          </p>
        </header>

        {/* Controls */}
        <div
          className="photographs-controls reveal"
          role="group"
          aria-label="View and frame controls"
        >
          {/* View density / columns */}
          {isMobile ? (
            <div className="view" role="group" aria-label="Mobile view density">
              <span className="view-label">View</span>
              <button
                type="button"
                onClick={() => setMobileCols(1)}
                className={
                  mobileCols === 1 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={mobileCols === 1}
              >
                Single
              </button>
              <button
                type="button"
                onClick={() => setMobileCols(2)}
                className={
                  mobileCols === 2 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={mobileCols === 2}
              >
                Duo
              </button>
            </div>
          ) : (
            <div
              className="view"
              role="group"
              aria-label="Desktop view density"
            >
              <span className="view-label">View</span>
              <button
                type="button"
                onClick={() => setDesktopCols(2)}
                className={
                  desktopCols === 2 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={desktopCols === 2}
              >
                Duo
              </button>
              <button
                type="button"
                onClick={() => setDesktopCols(3)}
                className={
                  desktopCols === 3 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={desktopCols === 3}
              >
                Trio
              </button>
            </div>
          )}

          {/* Frame toggle */}
          <label className="frame-toggle" aria-label="Framing preview toggle">
            <input
              type="checkbox"
              checked={framed}
              onChange={(e) => {
                const next = e.target.checked;
                setFramed(next);
                setDidInitFrame(true); // user touched it
              }}
            />
            <span>Framing Preview</span>
          </label>

          {/* Mat size toggle — only if framed */}
          {framed && (
            <div className="mat-toggle" aria-label="Matting size options">
              <span className="view-label">Matting</span>
              <button
                type="button"
                onClick={() => setMatSize("standard")}
                className={
                  matSize === "standard"
                    ? "view-spn selected-btn"
                    : "view-spn"
                }
                aria-pressed={matSize === "standard"}
              >
                Standard
              </button>
              <button
                type="button"
                onClick={() => setMatSize("large")}
                className={
                  matSize === "large" ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={matSize === "large"}
              >
                Gallery
              </button>
            </div>
          )}
        </div>

        {/* GRID */}
        <div className={gridClasses}>
          {list.length === 0 ? (
            <div
              style={{
                fontFamily: "Inter, system-ui, sans-serif",
                fontSize: "12px",
                letterSpacing: ".08em",
                textTransform: "uppercase",
                textAlign: "center",
                color: "rgba(0,0,0,.45)",
                padding: "40px 0 80px",
                width: "100%",
                gridColumn: "1 / -1",
              }}
            >
            
            </div>
          ) : (
              list.map((p) => {
    const {
      _id,
      imageURL,
      title,
      series,
      location,
      year,
      landscape,
    } = p || {};
    const key = _id || imageURL;
    if (!imageURL) return null;

    // Preload guess:
    // landscape true  -> ~3:2 => 66.666%
    // landscape false -> ~2:3 => 150%
    const placeholderPB = landscape ? "66.666%" : "150%";
              return (
                <Link
                  to={`/photographs/${_id}`}
                  key={key}
                  className="card-link reveal"
                  data-shape=""
                >
                  <article
                    className={[
                      "ph-card",
                      framed ? "has-frame" : "",
                    ].join(" ")}
                  >
                    <div className="ph-media">
                      <div className="ph-media-inner">
                        <img
                          className="ph-img"
                          src={imageURL}
                          alt={title || "Portrait"}
                          loading="lazy"
                          decoding="async"
                          onLoad={handleImgLoad}
                          onError={handleImgLoad}
                        />
                      </div>
                    </div>

                    <div className="ph-meta">
                      {series ? (
                        <div className="ph-series text-gray">{series}</div>
                      ) : null}
                      {title ? (
                        <div className="ph-title-case">{title}</div>
                      ) : null}

                      <div className="ph-lines">
                        {location ? (
                          <div className="ph-location text-gray-2">
                            {location}
                          </div>
                        ) : null}
                        {year ? (
                          <div className="ph-year italic">{year}</div>
                        ) : null}
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })
          )}
        </div>
      </div>

      {/* AudioFloat: always render now */}
      <AudioFloat
        srcs={[
          {
            src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/shop.mp3",
            type: "audio/mpeg",
          },
        ]}
        startMuted={false}
        startVolume={0.5}
        loop={true}
        label="Site background audio"
        startTime={190}
      />
    </div>
  );
}
